import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useRevokeLicense } from '@utils/api/back-office/licensing';
import { LicenseKey } from '@utils/types/back-office/licensing';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  licenseKey: LicenseKey | null;
}

export default function RevokeLicenseModal({
  isOpen,
  onClose,
  licenseKey
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const revokeLicense = useRevokeLicense();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRevoke = async () => {
    if (!licenseKey) return;

    setIsSubmitting(true);
    try {
      await revokeLicense.mutateAsync(licenseKey.id);
      onClose();
    } catch {
      // Error is handled in the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!licenseKey) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="small"
      title={t('revoke-license')}
      footerActions={[
        <Button
          key="cancel"
          onClick={onClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isSubmitting}
        />,
        <Button
          key="revoke"
          onClick={handleRevoke}
          label={isSubmitting ? t('revoking') : t('revoke')}
          size="small"
          variant="danger"
          disabled={isSubmitting}
        />
      ]}
    >
      <div className="space-y-4">
        {/* Warning */}
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-500/10 dark:bg-red-500/15">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">
              {t('confirm-revoke-license')}
            </p>
            <p className="text-xs mt-1 text-red-700 dark:text-red-400">
              {t('revoke-license-warning')}
            </p>
          </div>
        </div>

        {/* License Info */}
        <div className="p-3 rounded-2xl border border-hairline bg-app text-sm">
          <p style={{ color: getColour('inactiveText') }}>
            <span
              className="font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('licensee')}:
            </span>{' '}
            {licenseKey.licensee}
          </p>
          <p style={{ color: getColour('inactiveText') }}>
            <span
              className="font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('license-id')}:
            </span>{' '}
            #{licenseKey.id}
          </p>
          <p style={{ color: getColour('inactiveText') }}>
            <span
              className="font-medium"
              style={{ color: getColour('normalText') }}
            >
              {t('limits')}:
            </span>{' '}
            {licenseKey.org_limit ?? '∞'} {t('orgs')} /{' '}
            {licenseKey.user_limit ?? '∞'} {t('users')}
          </p>
        </div>
      </div>
    </Modal>
  );
}
